// @ts-strict-ignore
import { FetchInvalidPayloadShapeError } from "@lib/errors";
import type { RemoveFirstArgument } from "@lib/typeHelpers";

import type { dashboardApiEndpoints } from "../dashboardApiEndpoints";
import { reactQueryFetcherAdapter } from "../reactQueryFetcherAdapters";
import type { EndpointQueryKey, FilteredDashboardApiEndpointKeys } from "../types";
import type { GetFakeThingProps } from "./types";

/**
 * A fake fetcher function maker, for use in storybook
 *
 * @example
 * export const getFakeDeveloper = (maker: (companyId: string) => Partial<Developer>) =>
 *  getFakeThing({
 *    cacheKey,
 *    baseFakeThing: fakeDeveloper,
 *    maker,
 *  });
 */
export const getFakeSuccessGetFetcherFunction =
  <
    PayloadShape,
    CacheKey extends FilteredDashboardApiEndpointKeys,
    Args extends RemoveFirstArgument<Parameters<(typeof dashboardApiEndpoints)[CacheKey]["GET"]>[0]["queryKey"]>,
  >({
    cacheKey,
    baseFakeThing,
    validator,
  }: GetFakeThingProps<PayloadShape, CacheKey>) =>
  (
    maker?: (...args: Args) => Partial<PayloadShape>
  ): (({ queryKey: [, ...rest], signal }: EndpointQueryKey<CacheKey, Args>) => Promise<PayloadShape>) =>
    reactQueryFetcherAdapter(cacheKey, async (_signal, ...args): Promise<PayloadShape> => {
      const override = maker?.(...args);
      const newValue = Array.isArray(baseFakeThing)
        ? ((override || baseFakeThing) as PayloadShape)
        : {
            ...baseFakeThing,
            ...override,
          };

      FetchInvalidPayloadShapeError.validateShape({ cacheKey, result: newValue, validator });

      return new Promise((resolve) => setTimeout(() => resolve(newValue), 500));
    });
